<template>
  <div class="pdf-editor" ref="editorContainer">
    <!-- Toolbar -->
    <div class="editor-toolbar">
      <div class="toolbar-group">
        <button class="tb-btn" @click="scrollToPrevPage" :disabled="currentPage <= 1">
          <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"/></svg>
        </button>
        <span class="page-info">
          {{ currentPage }} / {{ totalPages }}
          <span v-if="currentPageSigCount > 0" class="page-sig-badge">{{ currentPageSigCount }}</span>
        </span>
        <button class="tb-btn" @click="scrollToNextPage" :disabled="currentPage >= totalPages">
          <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"/></svg>
        </button>
      </div>
      <div class="toolbar-group">
        <button class="tb-btn" @click="zoomOut" :disabled="scale <= 0.5">
          <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"/><path fill-rule="evenodd" d="M5 8a1 1 0 011-1h4a1 1 0 110 2H6a1 1 0 01-1-1z"/></svg>
        </button>
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        <button class="tb-btn" @click="zoomIn" :disabled="scale >= 3">
          <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"/><path fill-rule="evenodd" d="M8 5a1 1 0 011 1v1h1a1 1 0 110 2H9v1a1 1 0 11-2 0V9H6a1 1 0 010-2h1V6a1 1 0 011-1z"/></svg>
        </button>
      </div>
      <button class="tb-btn tb-btn--accent" @click="$refs.fileInput.click()">
        <svg viewBox="0 0 20 20" fill="currentColor"><path d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z"/></svg>
        Đổi file
      </button>
      <input ref="fileInput" type="file" accept="application/pdf" class="sr-only" @change="$emit('change-file', $event)">
    </div>

    <!-- PDF All Pages Scrollable -->
    <div class="editor-viewport" ref="viewport" @scroll="onScroll">
      <div
        v-for="pageNum in totalPages"
        :key="pageNum"
        class="page-container"
        :ref="'page_' + pageNum"
      >
        <div class="page-label">Trang {{ pageNum }}</div>
        <div class="page-canvas-wrap" :style="getPageWrapStyle(pageNum)">
          <canvas :ref="'canvas_' + pageNum"></canvas>

          <!-- Signatures for this page -->
          <div
            v-for="sig in getPageSignatures(pageNum)"
            :key="sig.id"
            class="sig-box"
            :class="{
              dragging: dragState.sigId === sig.id && dragState.isDragging,
              'has-image': sig.type !== 'text' && (!!sig.imageBase64 || !!signatureImage),
              'is-text': sig.type === 'text',
              selected: selectedSigId === sig.id
            }"
            :style="getSigBoxStyle(sig, pageNum)"
            @mousedown.prevent="startDrag($event, sig.id, pageNum)"
            @touchstart.prevent="startDrag($event, sig.id, pageNum)"
            @click.stop="selectSignature(sig.id)"
          >
            <!-- Text annotation -->
            <div v-if="sig.type === 'text'" class="sig-text-content" :style="{ fontSize: ((sig.fontSize || 13) * scale) + 'px' }">
              <span v-if="sig.textContent">{{ sig.textContent }}</span>
              <span v-else class="sig-text-placeholder">Nhập văn bản...</span>
            </div>
            <!-- Signature with image -->
            <img
              v-else-if="sig.imageBase64 || signatureImage"
              :src="'data:image/png;base64,' + (sig.imageBase64 || signatureImage)"
              class="sig-img"
              draggable="false"
            >
            <!-- Signature placeholder -->
            <div v-else class="sig-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
              <span>Kéo để đặt vị trí</span>
            </div>

            <!-- Delete button -->
            <button class="sig-delete" @mousedown.stop @click.stop="$emit('delete-signature', sig.id)">
              <svg viewBox="0 0 16 16" fill="currentColor"><path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/></svg>
            </button>

            <!-- Signature index label -->
            <span class="sig-index">#{{ getSignatureIndex(sig.id) }}</span>

            <!-- Resize handle -->
            <div class="sig-resize" @mousedown.stop.prevent="startResize($event, sig.id, pageNum)" @touchstart.stop.prevent="startResize($event, sig.id, pageNum)"></div>
          </div>
        </div>
      </div>

      <!-- Floating add buttons -->
      <div class="fab-group">
        <button class="fab-add" @click.stop="$emit('add-signature', currentPage, 'signature')" title="Thêm chữ ký">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
        </button>
        <button class="fab-add fab-add--text" @click.stop="$emit('add-signature', currentPage, 'text')" title="Thêm văn bản">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 4v3h5.5v12h3V7H19V4z"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PdfSignatureEditor',

  props: {
    pdfBase64: { type: String, default: null },
    signatureImage: { type: String, default: null },
    signatures: { type: Array, default: function () { return [] } },
    selectedSigId: { default: null }
  },

  data: function () {
    return {
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.5,
      pageDims: {},
      dragState: {
        sigId: null,
        isDragging: false,
        isResizing: false,
        startX: 0,
        startY: 0,
        startPosX: 0,
        startPosY: 0,
        startW: 0,
        startH: 0,
        pageNum: 1
      },
      scrollDetectTimer: null
    }
  },

  computed: {
    currentPageSigCount: function () {
      return this.getPageSignatures(this.currentPage).length
    }
  },

  watch: {
    pdfBase64: {
      immediate: true,
      handler: function (val) {
        if (val) this.loadPdf(val)
      }
    }
  },

  methods: {
    getPageDims: function (pageNum) {
      return this.pageDims[pageNum] || { width: 595, height: 842 }
    },

    getPageWrapStyle: function (pageNum) {
      var dims = this.getPageDims(pageNum)
      return {
        width: (dims.width * this.scale) + 'px',
        height: (dims.height * this.scale) + 'px'
      }
    },

    getPageSignatures: function (pageNum) {
      return this.signatures.filter(function (s) { return s.page === pageNum })
    },

    getSigBoxStyle: function (sig, pageNum) {
      var dims = this.getPageDims(pageNum)
      var s = this.scale
      var left = sig.x * s
      var top = (dims.height - sig.y - sig.height) * s
      return {
        left: left + 'px',
        top: top + 'px',
        width: (sig.width * s) + 'px',
        height: (sig.height * s) + 'px'
      }
    },

    getSignatureIndex: function (sigId) {
      var idx = this.signatures.findIndex(function (s) { return s.id === sigId })
      return idx + 1
    },

    selectSignature: function (sigId) {
      this.$emit('select-signature', sigId)
    },

    goToPage: function (num) {
      this.scrollToPage(num)
    },

    // --- PDF Loading ---
    loadPdf: async function (base64) {
      try {
        // Dùng cùng cách load pdfjsLib như PdfEditorTest (qua CDN script)
        var getAsset = (await import('@/components/pdfEditor/utils/prepareAssets')).getAsset
        var pdfjsLib = await getAsset('pdfjsLib')

        var data = atob(base64)
        var uint8 = new Uint8Array(data.length)
        for (var i = 0; i < data.length; i++) uint8[i] = data.charCodeAt(i)

        this.pdfDoc = await pdfjsLib.getDocument({ data: uint8 }).promise
        this.totalPages = this.pdfDoc.numPages
        this.currentPage = 1
        await this.renderAllPages()
      } catch (e) {
        console.error('[PdfSignatureEditor] PDF load error:', e)
      }
    },

    renderAllPages: async function () {
      if (!this.pdfDoc) return
      var self = this
      Object.keys(this._renderTasks || {}).forEach(function(k) {
        try { self._renderTasks[k].cancel() } catch(e) { /* ignore */ }
      })
      this._renderTasks = {}
      for (var num = 1; num <= this.totalPages; num++) {
        await this.renderSinglePage(num)
      }
    },

    renderSinglePage: async function (num) {
      if (!this.pdfDoc) return
      if (!this._renderTasks) this._renderTasks = {}

      var page = await this.pdfDoc.getPage(num)
      var baseViewport = page.getViewport({ scale: 1 })
      var viewport = page.getViewport({ scale: this.scale })

      this.$set(this.pageDims, num, {
        width: baseViewport.width,
        height: baseViewport.height
      })

      var canvasRef = this.$refs['canvas_' + num]
      var canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
      if (!canvas) return

      if (this._renderTasks[num]) {
        try { this._renderTasks[num].cancel() } catch(e) { /* ignore */ }
        delete this._renderTasks[num]
      }

      var ctx = canvas.getContext('2d')
      canvas.width = viewport.width
      canvas.height = viewport.height

      var task = page.render({ canvasContext: ctx, viewport: viewport })
      this._renderTasks[num] = task
      try {
        await task.promise
      } catch (e) {
        if (e && e.name === 'RenderingCancelledException') return
        throw e
      } finally {
        delete this._renderTasks[num]
      }
    },

    // --- Scroll / Page Detection ---
    onScroll: function () {
      var self = this
      if (this.scrollDetectTimer) clearTimeout(this.scrollDetectTimer)
      this.scrollDetectTimer = setTimeout(function () {
        self.detectCurrentPage()
      }, 100)
    },

    detectCurrentPage: function () {
      var viewport = this.$refs.viewport
      if (!viewport) return
      var viewportRect = viewport.getBoundingClientRect()
      var viewportMid = viewportRect.top + viewportRect.height / 2
      var bestPage = 1
      var bestDist = Infinity

      for (var num = 1; num <= this.totalPages; num++) {
        var ref = this.$refs['page_' + num]
        var el = Array.isArray(ref) ? ref[0] : ref
        if (!el) continue
        var rect = el.getBoundingClientRect()
        var pageMid = rect.top + rect.height / 2
        var dist = Math.abs(pageMid - viewportMid)
        if (dist < bestDist) {
          bestDist = dist
          bestPage = num
        }
      }
      this.currentPage = bestPage
    },

    scrollToPage: function (num) {
      var ref = this.$refs['page_' + num]
      var el = Array.isArray(ref) ? ref[0] : ref
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.currentPage = num
      }
    },

    scrollToPrevPage: function () {
      if (this.currentPage > 1) this.scrollToPage(this.currentPage - 1)
    },

    scrollToNextPage: function () {
      if (this.currentPage < this.totalPages) this.scrollToPage(this.currentPage + 1)
    },

    zoomIn: function () {
      this.scale = Math.min(3, this.scale + 0.25)
      this.renderAllPages()
    },

    zoomOut: function () {
      this.scale = Math.max(0.5, this.scale - 0.25)
      this.renderAllPages()
    },

    // --- DRAG ---
    startDrag: function (e, sigId, pageNum) {
      var sig = this.signatures.find(function (s) { return s.id === sigId })
      if (!sig) return

      this.$emit('select-signature', sigId)

      var dims = this.getPageDims(pageNum)
      this.dragState.sigId = sigId
      this.dragState.isDragging = true
      this.dragState.pageNum = pageNum
      var ev = e.touches ? e.touches[0] : e
      this.dragState.startX = ev.clientX
      this.dragState.startY = ev.clientY
      this.dragState.startPosX = sig.x
      this.dragState.startPosY = sig.y

      var self = this
      var onMove = function (me) {
        var mv = me.touches ? me.touches[0] : me
        var dx = (mv.clientX - self.dragState.startX) / self.scale
        var dy = (mv.clientY - self.dragState.startY) / self.scale
        var newX = Math.max(0, Math.min(dims.width - sig.width, self.dragState.startPosX + dx))
        var newY = Math.max(0, Math.min(dims.height - sig.height, self.dragState.startPosY - dy))
        self.$emit('update-signature', sigId, { x: Math.round(newX), y: Math.round(newY) })
      }

      var onUp = function () {
        self.dragState.isDragging = false
        self.dragState.sigId = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove)
      document.addEventListener('touchend', onUp)
    },

    // --- RESIZE ---
    startResize: function (e, sigId, pageNum) {
      var sig = this.signatures.find(function (s) { return s.id === sigId })
      if (!sig) return

      this.$emit('select-signature', sigId)
      this.dragState.sigId = sigId
      this.dragState.isResizing = true
      this.dragState.pageNum = pageNum
      var ev = e.touches ? e.touches[0] : e
      this.dragState.startX = ev.clientX
      this.dragState.startY = ev.clientY
      this.dragState.startW = sig.width
      this.dragState.startH = sig.height

      var self = this
      var onMove = function (me) {
        var mv = me.touches ? me.touches[0] : me
        var dx = (mv.clientX - self.dragState.startX) / self.scale
        var dy = (mv.clientY - self.dragState.startY) / self.scale
        var newW = Math.max(40, self.dragState.startW + dx)
        var newH = Math.max(20, self.dragState.startH + dy)
        self.$emit('update-signature', sigId, { width: Math.round(newW), height: Math.round(newH) })
      }

      var onUp = function () {
        self.dragState.isResizing = false
        self.dragState.sigId = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove)
      document.addEventListener('touchend', onUp)
    }
  }
}
</script>

<style scoped>
.pdf-editor {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  background: #e9ecef;
}

.sr-only { position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0); }

/* Toolbar */
.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}
.toolbar-group {
  display: flex;
  align-items: center;
  gap: 2px;
  background: #f1f3f5;
  border-radius: 6px;
  padding: 2px;
}
.tb-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.tb-btn:hover:not(:disabled) { background: #e9ecef; color: #212529; }
.tb-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.tb-btn svg { width: 16px; height: 16px; }
.tb-btn--accent {
  width: auto;
  padding: 0 10px;
  gap: 4px;
  font-size: 12px;
  font-family: inherit;
  margin-left: auto;
  color: #00568c;
}
.tb-btn--accent:hover { background: #e6f2f8; }

.page-info, .zoom-info {
  font-size: 12px;
  color: #6c757d;
  padding: 0 8px;
  font-family: 'JetBrains Mono', monospace;
  min-width: 50px;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-sig-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #0071b8;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

/* Viewport */
.editor-viewport {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 24px;
  position: relative;
}

.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.page-label {
  font-size: 11px;
  color: #6060a0;
  margin-bottom: 6px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.page-canvas-wrap {
  position: relative;
  box-shadow: 0 4px 24px rgba(0,0,0,0.5);
  flex-shrink: 0;
}

.page-canvas-wrap canvas {
  display: block;
  width: 100%;
  height: 100%;
}

/* Signature Box */
.sig-box {
  position: absolute;
  border: 2px dashed #0071b8;
  background: rgba(59, 130, 246, 0.08);
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: box-shadow 0.2s, border-color 0.2s;
  user-select: none;
  z-index: 10;
}
.sig-box:hover {
  border-color: #3d9fd4;
  box-shadow: 0 0 0 3px rgba(0,113,184,0.2);
}
.sig-box.selected {
  border-color: #00568c;
  box-shadow: 0 0 0 3px rgba(0,86,140,0.35);
  z-index: 12;
}
.sig-box.dragging {
  cursor: grabbing;
  border-color: #00568c;
  box-shadow: 0 0 0 4px rgba(0,86,140,0.3);
  opacity: 0.85;
}
.sig-box.has-image {
  border-color: #22c55e;
  background: rgba(255,255,255,0.9);
}
.sig-box.has-image:hover {
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(34,197,94,0.2);
}
.sig-box.has-image.selected {
  border-color: #15803d;
  box-shadow: 0 0 0 3px rgba(34,197,94,0.35);
}

/* Text Annotation Box */
.sig-box.is-text {
  border-color: #e67e22;
  background: rgba(230,126,34,0.05);
}
.sig-box.is-text:hover {
  border-color: #d35400;
  box-shadow: 0 0 0 3px rgba(230,126,34,0.2);
}
.sig-box.is-text.selected {
  border-color: #d35400;
  box-shadow: 0 0 0 3px rgba(230,126,34,0.35);
}
.sig-box.is-text .sig-index { background: #e67e22; }
.sig-box.is-text .sig-resize { background: #e67e22; }

.sig-text-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 4px;
  pointer-events: none;
  color: #1e293b;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.2;
  font-family: 'Roboto', system-ui, sans-serif;
}
.sig-text-placeholder {
  color: #94a3b8;
  font-style: italic;
  font-weight: 400;
}

.sig-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.sig-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  pointer-events: none;
}
.sig-placeholder svg {
  width: 20px;
  height: 20px;
  color: #3d9fd4;
}
.sig-placeholder span {
  font-size: 10px;
  color: #3d9fd4;
  font-weight: 500;
  white-space: nowrap;
}

/* Delete Button */
.sig-delete {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: #ef4444;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: transform 0.15s;
  z-index: 15;
  padding: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}
.sig-delete:hover {
  transform: scale(1.15);
  background: #dc2626;
}

/* Signature Index Label */
.sig-index {
  position: absolute;
  top: -10px;
  left: -4px;
  background: #00568c;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  pointer-events: none;
  line-height: 1.4;
  z-index: 14;
}
.sig-box.has-image .sig-index { background: #16a34a; }

/* Resize Handle */
.sig-resize {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 12px;
  height: 12px;
  background: #00568c;
  border: 2px solid #fff;
  border-radius: 2px;
  cursor: se-resize;
  z-index: 11;
}
.sig-box.has-image .sig-resize { background: #16a34a; }

/* Floating Add Buttons */
.fab-group {
  position: sticky;
  bottom: 16px;
  align-self: flex-end;
  margin-right: 16px;
  display: flex;
  gap: 8px;
  z-index: 20;
  flex-shrink: 0;
}
.fab-add {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #00568c;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0,113,184,0.4);
  transition: all 0.2s;
  flex-shrink: 0;
}
.fab-add:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 24px rgba(0,113,184,0.5);
}
.fab-add svg { width: 20px; height: 20px; }
.fab-add--text {
  background: #e67e22;
  box-shadow: 0 4px 16px rgba(230,126,34,0.4);
}
.fab-add--text:hover {
  box-shadow: 0 6px 24px rgba(230,126,34,0.5);
}

/* Scrollbar */
.editor-viewport::-webkit-scrollbar { width: 8px; height: 8px; }
.editor-viewport::-webkit-scrollbar-track { background: #e9ecef; }
.editor-viewport::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 4px; }
.editor-viewport::-webkit-scrollbar-thumb:hover { background: #adb5bd; }
</style>
